import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BigButton } from "@/components/Layout";
import { MapPicker } from "@/components/MapPicker";
import { AddressFields } from "@/components/Fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const LABELS = ["Home", "Office", "Shop", "Other"];

export const LabelPicker = ({ value, onChange }) => {
  const { t } = useApp();
  return (
    <div className="flex gap-2" data-testid="address-label-picker">
      {LABELS.map((l) => (
        <button key={l} type="button" onClick={() => onChange(l)} data-testid={`address-label-${l.toLowerCase()}`}
          className={`h-10 flex-1 rounded-full border-2 text-sm font-semibold transition-colors ${value === l ? "border-[#2E9E6B] bg-[#2E9E6B]/10 text-[#0D3B2B]" : "border-[#D2E0D8] bg-white text-[#4A6358]"}`}>{t(l)}</button>
      ))}
    </div>
  );
};

const blank = { label: "Home", address: "", landmark: "", lat: null, lng: null, is_default: false };

export const AddressForm = ({ open, onOpenChange, initial, onSaved }) => {
  const { t } = useApp();
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...blank, ...initial } : blank); }, [open, initial]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    if (f.lat == null) return toast.error(t("Please set the location on the map."));
    if (!f.address.trim()) return toast.error(t("Please enter the address."));
    setBusy(true);
    try {
      const body = { label: f.label, address: f.address, landmark: f.landmark, lat: f.lat, lng: f.lng, is_default: f.is_default };
      const { data } = initial ? await api.put(`/addresses/${initial.address_id}`, body) : await api.post("/addresses", body);
      toast.success(t("Address saved"));
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-[94vw] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="address-form-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-xl text-[#0D3B2B]">{t(initial ? "Edit address" : "Add a new address")}</DialogTitle>
          <DialogDescription>{t("Set the pin on the exact spot so our team can find you.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <LabelPicker value={f.label} onChange={(label) => set({ label })} />
          {open && <MapPicker lat={f.lat} lng={f.lng} autoDetect={!initial} onChange={(lat, lng) => set({ lat, lng })} />}
          <AddressFields form={f} set={set} />
          {!initial?.is_default && (
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0D3B2B]">
              <input type="checkbox" checked={f.is_default} onChange={(e) => set({ is_default: e.target.checked })} className="h-5 w-5 accent-[#2E9E6B]" data-testid="address-default-checkbox" />
              {t("Make this my default address")}
            </label>
          )}
          <BigButton onClick={save} disabled={busy} data-testid="address-save-btn">{busy ? t("Saving…") : t("Save address")}</BigButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
